import React, { useState } from 'react';
import { runInAction } from 'mobx';
import { Col, Form, Row } from 'antd';
import { observer } from 'mobx-react-lite';
import LoanLayout from '../LoanLayout';
import { useLoan } from '../../stores/store';
import Loading from '../Loading';
import BorrowerForm from '../components/BorrowerForm';
import BorrowerContactForm from '../components/BorrowerContactForm';
import ButtonNav from '../../components/ButtonNav/ButtonNav';
import { stepFinished } from '../../nav';
import { ApplicantType, Borrower, LoanPurpose } from '../../schema';
import Text from 'antd/lib/typography/Text';
import { StepFooter } from '../components/StepFooter/StepFooter';
import { useFeatureFlag } from '../../hooks/useFeatureFlag';
import { FF_FULL_APPLICATION_STYLING } from '../../constants';

type Props = {};

const BorrowerStep: React.FC<Props> = observer(() => {
  const [showPhoneNumberMessage, setShowPhoneNumberMessage] = useState(false);
  const [form] = Form.useForm();
  const loanStore = useLoan();
  const isNewFunnel = useFeatureFlag({
    flag: FF_FULL_APPLICATION_STYLING,
  });
  const { loan } = loanStore;

  if (!loan || loanStore.status === 'pending') {
    return <Loading />;
  }

  const showCoborrower = loan.applicantType === ApplicantType.joint;

  const borrower = loanStore.getBorrower();
  const coBorrower = loanStore.getCoBorrower();

  const onFinish = () => {
    if (
      loan.applicantType === ApplicantType.individual &&
      Object.keys(loan.coborrower as any).length > 0
    ) {
      runInAction(() => {
        loan.coborrower = {} as Borrower;
      });
    }

    // noinspection JSIgnoredPromiseFromCall
    stepFinished();
  };

  const validatePhoneFields: React.FormEventHandler<HTMLFormElement> = (e) => {
    const homePhone = form.getFieldValue('homePhone');
    const cellPhone = form.getFieldValue('cellPhone');

    if (showCoborrower && !homePhone && !cellPhone) {
      e.preventDefault();
      e.stopPropagation();
      setShowPhoneNumberMessage(true);
    } else {
      setShowPhoneNumberMessage(false);
    }
  };

  const onValueChange = (changedValues: any) => {
    if (['homePhone', 'cellPhone'].includes(Object.keys(changedValues)[0])) {
      setShowPhoneNumberMessage(false);
    }
  };

  return (
    <LoanLayout>
      {isNewFunnel && <div className="step-title">About You</div>}
      <h1>
        {loan?.purpose === LoanPurpose.boat ? 'Tell us a little about you' : 'Your Information'}
      </h1>
      {loan?.purpose === LoanPurpose.boat && (
        <p>Enter your information for your loan application</p>
      )}
      <Form
        form={form}
        onValuesChange={onValueChange}
        onFinish={onFinish}
        onSubmitCapture={validatePhoneFields}
      >
        <BorrowerForm borrower={borrower} showApplicationType showPrivacyPolicy />
        {showCoborrower && (
          <>
            <h1 style={{ marginTop: '0.5rem' }}>Your Co-Borrower&apos;s Information</h1>
            <BorrowerForm
              formItemPrefix="co-"
              borrower={coBorrower}
              showRelationshipField
              showPrivacyPolicy
              isCoBorrower
            />
            <h1 style={{ marginTop: '0.5rem' }}>How do we contact your Co-Borrower?</h1>
            <BorrowerContactForm borrower={coBorrower} form={form} showPrivacyPolicy isCoBorrower />
            {showPhoneNumberMessage && (
              <Row>
                <Col sm={0} md={4} />
                <Text type="danger" id="no-phone-message">
                  You must enter a phone number.
                </Text>
              </Row>
            )}
          </>
        )}
        <ButtonNav />
        <StepFooter type="secure" />
      </Form>
    </LoanLayout>
  );
});

export default BorrowerStep;
